import { type DocNode } from '@atlaskit/adf-schema/doc';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	isConversationChannelDataMessage,
	isSSEMessage,
	isStreamAgenticUi,
	isStreamAnswerPart,
	isStreamAUPError,
	isStreamError,
	isStreamResponse,
	type StreamAgenticUi,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	CHAT_STORE_STATUS,
	type Conversation,
	StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { addAgenticUiMessage } from './addAgenticUiMessage';
import { addAnswerPart } from './addAnswerPart';
import { addAnswerPartWithMarkdownDataMap } from './addAnswerPartWithMarkdownDataMap';
import { addAssistantMessage } from './addAssistantMessage';
import { clearStatus } from './clearStatus';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { createStreamMarkdownMultiTagProcessor } from './createStreamMarkdownMultiTagProcessor';
import { NoFinalResponseError } from './error-classes/NoFinalResponseError';
import { StreamErrorException } from './error-classes/StreamErrorException';
import { extractStreamMeta } from './extractStreamMeta';
import { generateTmpMessageId } from './generateTmpMessageId';
import { markStreamingMessagesAsComplete } from './markStreamingMessagesAsComplete';
import { processStreamResponseMarkdown } from './processStreamResponseMarkdown';
import { replaceAnswerData } from './replaceAnswerData';
import { setConversationData } from './setConversationData';
import { shouldPreprocessDetailsForConversation } from './shouldPreprocessDetailsForConversation';
import type { StreamMeta } from './StreamMeta';
import { createStoreAbortController } from './store-abort-controller';
import type { ChatStoreAction, MessageSource } from './types';

export type A2UIActionPayload = {
	eventName: string;
	context?: Record<string, unknown>;
	dataModel?: Record<string, unknown>;
};

export type SendA2UIActionArgs = {
	action: A2UIActionPayload;
	/** The assistant message hosting the surface the action was raised from. */
	hostMessageId: string;
	conversationId?: string;
	agentId?: string;
	webSearchEnabled?: boolean;
	useCurrentBrowsingContext?: boolean;
	appFilters?: string[];
	messageSources?: Partial<MessageSource>;
};

const A2UI_ACTION_MIME_TYPE = 'text/json';

const toStructuredActionContent = (action: A2UIActionPayload, hostMessageId: string) => ({
	data_parts_mime_type: 'application/json+a2ui',
	structured: true,
	data_parts: [
		{
			userAction: {
				actionName: action.eventName,
				context: {
					...(action.context ?? action.dataModel ?? {}),
					assistantMessageId: hostMessageId,
				},
			},
		},
	],
});

/**
 * Sends a user action raised from an A2UI surface — a button pressed, a form
 * submitted — and applies the agent's response to the chat.
 *
 * The response is split by what it carries, and the two halves land in different
 * places. AGENTIC_UI ops are re-rendered into `hostMessageId`, the message that
 * already owns the surface the action came from, so the surface updates in place.
 * Reply text (ANSWER_PART, or FINAL_RESPONSE content when nothing streamed) belongs
 * to this action's own turn instead, and is written to a new assistant message.
 *
 * That new message is inserted lazily, only once reply text actually arrives: most
 * surface actions answer with surface ops alone, and inserting up front would leave
 * an empty bubble behind. So nothing is inserted at send time, and a response that
 * only drives the surface leaves the chat untouched.
 */
export const sendA2UIAction: SafeAction<[SendA2UIActionArgs]> = createSafeAction(
	StoreActionLoggableName.sendA2UIAction,
	({
		action,
		hostMessageId,
		conversationId: conversationIdParam,
		agentId,
		webSearchEnabled,
		useCurrentBrowsingContext,
		appFilters,
		messageSources,
	}: SendA2UIActionArgs): ChatStoreAction =>
		async (
			{ getState, dispatch },
			{
				assistanceService,
				experienceTracker,
				getMessages,
				setMessages,
				trackAIMAUEvent,
				sendAnalyticsEvent,
			},
		) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;
			const mentionAgentsEnabled = isExperimentEnabled('rovo_chat_mention_agents');
			const tmpAssistantMessageId = generateTmpMessageId(conversationId);
			const { abortController, storeAbortController, confirmAbort } = createStoreAbortController();

			let hasInsertedTurnAssistantMessage = false;
			let agentMessageId: string | undefined;
			let requestId: string | undefined;
			let humanMessageId: string | undefined;
			let streamMeta: StreamMeta | undefined;
			let initiatedTimestamp: string | undefined;
			let hasLoggedInitiated = false;
			let hasReceivedFinalResponse = false;

			const updateCurrentConversation = (data: Partial<Conversation>) => {
				dispatch(setConversationData({ conversationId, data }));
			};

			const instrumentationId = () => agentMessageId ?? tmpAssistantMessageId;

			const trackInitiated = () => {
				if (hasLoggedInitiated) {
					return;
				}
				hasLoggedInitiated = true;
				trackAIMAUEvent({
					type: 'initiated',
					subjectId: messageSources?.interactionSource,
					proactive: false,
					userGenerated: true,
					timestamp: initiatedTimestamp,
					singleInstrumentationID: instrumentationId(),
					webSearchEnabled,
					attributes: { sendMessageReason: 'A2UI_ACTION', actionName: action.eventName },
				});
			};

			const trackViewed = () => {
				trackAIMAUEvent({
					type: 'viewed',
					proactive: false,
					userGenerated: true,
					source: messageSources?.interactionSource,
					subjectId: messageSources?.followUpSource,
					messageId: agentMessageId,
					singleInstrumentationID: instrumentationId(),
					timestamp: new Date().toISOString(),
					webSearchEnabled,
					citedProductList: streamMeta?.citedProductList ?? {},
					sourceProducts: streamMeta?.sourceProducts ?? {},
					connectorInfo: streamMeta?.connectorInfo,
					responseSources: streamMeta?.responseSources,
					usesJsm: streamMeta?.usesJsm,
					messageActions: streamMeta?.messageActions,
					messageDynamicUIData: streamMeta?.messageDynamicUIData,
					invokedFrom: streamMeta?.invokedFrom,
					pluginsInvoked: streamMeta?.pluginsInvoked,
					skillsInvoked: streamMeta?.skillsInvoked,
					executedStratusSkill: streamMeta?.executedStratusSkill,
					attributes: {
						sendMessageReason: 'A2UI_ACTION',
						actionName: action.eventName,
						/** Whether the response updated the surface only, without opening a message. */
						surfaceOnlyResponse: !hasInsertedTurnAssistantMessage,
						...(streamMeta?.interactionModeExecuted
							? { interactionModeExecuted: streamMeta.interactionModeExecuted }
							: {}),
					},
				});
			};

			const trackTerminal = (type: 'error' | 'dismissed') => {
				trackInitiated();
				trackAIMAUEvent({
					type,
					userGenerated: true,
					messageId: requestId,
					subjectId: messageSources?.interactionSource,
					singleInstrumentationID: instrumentationId(),
					timestamp: new Date().toISOString(),
					webSearchEnabled,
					attributes: { sendMessageReason: 'A2UI_ACTION', actionName: action.eventName },
				});
			};

			/**
			 * Inserts this turn's assistant message, once, on the first reply text to arrive.
			 */
			const insertTurnAssistantMessage = () => {
				if (hasInsertedTurnAssistantMessage) {
					return;
				}
				hasInsertedTurnAssistantMessage = true;
				dispatch(
					addAssistantMessage({
						id: tmpAssistantMessageId,
						content: '',
						conversationId,
						...(mentionAgentsEnabled ? { agentAuthorId: agentId } : { agentNamedId: agentId }),
					}),
				);
			};

			// Rewrites `<details>`/`<summary>` into the Rovo-native expand boundary — third-party
			// agents lean on it heavily. Built on the first chunk rather than here so the
			// conversation's agent metadata is settled by the time it decides.
			let markdownMultiTagProcessor: ReturnType<
				typeof createStreamMarkdownMultiTagProcessor
			> | null = null;
			const getMarkdownMultiTagProcessor = () => {
				if (!markdownMultiTagProcessor) {
					markdownMultiTagProcessor = createStreamMarkdownMultiTagProcessor({
						shouldPreprocessDetails: shouldPreprocessDetailsForConversation(
							getState(),
							conversationId,
						),
					});
				}
				return markdownMultiTagProcessor;
			};

			try {
				experienceTracker.start({
					name: ExperienceName.SEND_A2UI_ACTION,
					id: conversationId,
					attributes: { conversationId, interactionSource: messageSources?.interactionSource },
				});

				updateCurrentConversation({
					storeAbortController,
					status: CHAT_STORE_STATUS.awaitingResponse,
					lastAgentMessageStatus: 'IN_PROGRESS',
				});

				initiatedTimestamp = new Date().toISOString();
				const { generator, responseData } = await assistanceService.sendMessageStream({
					conversationId,
					// eslint-disable-next-line @atlaskit/platform/no-double-type-assertions -- Existing A2UI baseline: structured action content is adapted to the send pipeline's prompt type.
					message: toStructuredActionContent(action, hostMessageId) as unknown as DocNode,
					mimeType: A2UI_ACTION_MIME_TYPE,
					agentId,
					controller: abortController,
					storeMessage: false,
					citationsEnabled: true,
					webSearchEnabled,
					useCurrentBrowsingContext,
					appFilters,
					optInSSEStream: true,
				});

				for await (const streamObj of generator) {
					const streamMessage = isSSEMessage(streamObj) ? streamObj.data : streamObj;

					/**
					 * Surface ops render into the message that hosts the surface, never into this
					 * turn's own message — that is what keeps the action attributed to its round.
					 */
					if (isStreamAgenticUi(streamObj)) {
						const agenticUiMessage: StreamAgenticUi = {
							...streamObj.data,
							type: 'AGENTIC_UI',
						};
						setMessages(
							conversationId,
							addAgenticUiMessage(getMessages(conversationId), hostMessageId, agenticUiMessage),
						);
						continue;
					}

					if (isConversationChannelDataMessage(streamMessage)) {
						agentMessageId = streamMessage.assistant_message_id;
						trackInitiated();
					}

					if (isStreamAnswerPart(streamMessage)) {
						insertTurnAssistantMessage();
						const { processedStreamMessage, markdownData } =
							getMarkdownMultiTagProcessor()(streamMessage);
						const hasMarkdownData = Object.keys(markdownData).length > 0;
						setMessages(
							conversationId,
							hasMarkdownData
								? addAnswerPartWithMarkdownDataMap({
										chat: getMessages(conversationId),
										answerId: tmpAssistantMessageId,
										answerPart: processedStreamMessage,
										markdownData,
									})
								: addAnswerPart({
										chat: getMessages(conversationId),
										answerId: tmpAssistantMessageId,
										answerPart: processedStreamMessage,
									}),
						);
					}

					if (isStreamResponse(streamMessage)) {
						hasReceivedFinalResponse = true;
						requestId = streamMessage.metadata?.request_id ?? requestId;
						streamMeta = extractStreamMeta(
							streamMessage as StreamResponse,
							undefined,
							expValEquals('cc-maui-experiment', 'isEnabled', true),
						);
						humanMessageId = streamMeta.humanMessageId;
						const finalResponseMessageId = streamMessage.message?.message?.id?.toString();
						// Once `initiated` has been paired against an id, `viewed` has to carry the
						// same one, so the final response only supplies it when nothing has fired yet.
						if (!hasLoggedInitiated) {
							agentMessageId = finalResponseMessageId ?? agentMessageId;
						}

						const { processedStreamMessage, markdownData } = processStreamResponseMarkdown({
							streamMessage,
							messageId: finalResponseMessageId,
							conversationId,
							sendAnalyticsEvent,
							shouldPreprocessDetails: shouldPreprocessDetailsForConversation(
								getState(),
								conversationId,
							),
						});

						/**
						 * A reply whose text only arrives here still needs a message; one that
						 * carried nothing but surface ops must not get one.
						 */
						if (processedStreamMessage.message?.message?.content) {
							insertTurnAssistantMessage();
						}

						if (hasInsertedTurnAssistantMessage) {
							setMessages(
								conversationId,
								replaceAnswerData({
									chat: getMessages(conversationId),
									answerId: tmpAssistantMessageId,
									content: processedStreamMessage,
									humanMessageId,
									markdownData: Object.keys(markdownData).length > 0 ? markdownData : undefined,
									traceId: responseData?.traceId,
								}),
							);
						}
					}

					if (isStreamError(streamMessage)) {
						const { message, metadata, millisOffset, type } = streamMessage;

						// AUP violations are a valid outcome, so the experience still succeeds.
						if (isStreamAUPError(streamMessage)) {
							experienceTracker.succeed({ name: ExperienceName.SEND_A2UI_ACTION });
						}

						throw new StreamErrorException(message?.content || 'Unknown', {
							detail: { message, metadata, millisOffset, type },
							traceId: responseData?.traceId,
						});
					}
				}
			} catch (error) {
				if (error instanceof Error && error.name === 'AbortError') {
					experienceTracker.abort({ name: ExperienceName.SEND_A2UI_ACTION, reason: 'Aborted' });
					confirmAbort();
					trackTerminal('dismissed');
					return;
				}

				experienceTracker.fail({
					name: ExperienceName.SEND_A2UI_ACTION,
					error: error instanceof Error ? error : new Error(String(error)),
				});
				trackTerminal('error');
				throw error;
			} finally {
				updateCurrentConversation(clearStatus({}));
				setMessages(conversationId, markStreamingMessagesAsComplete(getMessages(conversationId)));
			}

			// A programmatic abort ends the generator cleanly rather than throwing.
			if (abortController.signal.aborted) {
				experienceTracker.abort({ name: ExperienceName.SEND_A2UI_ACTION, reason: 'Aborted' });
				confirmAbort();
				trackTerminal('dismissed');
				return;
			}

			if (!hasReceivedFinalResponse) {
				const error = new NoFinalResponseError(
					'The response took too long and timed out. Try refreshing the page.',
					{ traceId: undefined },
				);
				experienceTracker.fail({ name: ExperienceName.SEND_A2UI_ACTION, error });
				trackTerminal('error');
				throw error;
			}

			trackInitiated();
			trackViewed();
			experienceTracker.succeed({ name: ExperienceName.SEND_A2UI_ACTION });
		},
);
